// ══════════════════════════════════════════════════════════════════
// badgeGenerator.js — RITIS Employee ID Badge (print-ready, front + back)
//
// Portrait CR80 credit-card-sized (54mm × 85.6mm) two-sided bilingual
// ID badge PDF for a team member. Design approved with the client
// across several rounds of HTML/wireframe previews:
//   FRONT: company header (two-tier name), "EMPLOYEE ID CARD" subtitle,
//          framed square photo, English + Arabic name, position,
//          Nationality/Department/Blood Type grid, Employee ID,
//          "VALID UNTIL" date — all over a very light (7%) watermark.
//   BACK:  company name, "EMPLOYEE IDENTIFICATION CARD" subtitle, a
//          QR code, "Scan to Verify Employee", card-property
//          disclaimer, "IF FOUND, PLEASE RETURN TO" + phone/email/
//          website rows with icons — over a light (16%) watermark.
// Mirrors the on-screen preview in BadgeCardPreview.jsx — keep both
// in sync if either changes.
//
// Arabic text: jsPDF cannot shape/reorder Arabic script itself, so the
// Arabic name is rendered into an offscreen <canvas> (ctx.direction =
// 'rtl', a system font stack with full Arabic coverage) and embedded
// as a PNG via addImage — the same "render to canvas, addImage the
// dataURL" pattern already used below for photos/logos/watermarks.
//
// QR code: generated per-badge via the `qrcode` package (dataURL),
// encoding a placeholder "RITIS-EMPLOYEE:<employee id>" string. This
// is a visual/branding element only — there is no verification
// endpoint behind it yet.
// ══════════════════════════════════════════════════════════════════

import jsPDF from 'jspdf';
import QRCode from 'qrcode';

const COMPANY_NAME_L1 = 'AL-RAMMAH INTERNATIONAL';
const COMPANY_NAME_L2 = 'Telecom & Information System Services';
const CARD_DISCLAIMER = 'This card remains the property of the company and must be returned upon termination of employment.';
const CONTACT_ROWS = [
    { icon: 'phone', text: '+966 13 896 1700' },
    { icon: 'email', text: 'info@goritis.com' },
    { icon: 'globe', text: 'www.goritis.com' },
];

// Badge validity, used only when a member has no explicit badge_expiry_date.
const DEFAULT_VALIDITY_YEARS = 3;

export const BADGE_COLORS = {
    teal: '#5bbdb2',
    tealDark: '#2f8f86',
    tealChip: '#e6f5f3',
    ink: '#0f172a',
    sub: '#64748b',
    panelBg: '#1c4a41',     // dark shade of the logo's own green
    panelBorder: '#163c34',
    active: '#5bbdb2',
    inactive: '#94a3b8',
    onDarkMuted: '#bfd6d1', // muted white/teal for subordinate text on the dark back
};

const loadImageDataURL = (url, maxWidth = 700) => new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => resolve(null), 8000);
    img.onload = () => {
        clearTimeout(timer);
        try {
            const scale = Math.min(1, maxWidth / img.width);
            const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, w, h);
            resolve({ dataUrl: canvas.toDataURL('image/png'), aspectRatio: img.width / img.height });
        } catch { resolve(null); }
    };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = url;
});

const loadSquarePhotoDataURL = (url, size = 300) => new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => resolve(null), 8000);
    img.onload = () => {
        clearTimeout(timer);
        try {
            const canvas = document.createElement('canvas');
            canvas.width = size; canvas.height = size;
            const ctx = canvas.getContext('2d');
            const side = Math.min(img.width, img.height);
            const sx = (img.width - side) / 2;
            const sy = (img.height - side) / 2;
            ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
            resolve(canvas.toDataURL('image/png'));
        } catch { resolve(null); }
    };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = url;
});

// Renders Arabic (or any RTL) text into an offscreen canvas using the
// browser's own text shaping/bidi engine — jsPDF's native doc.text()
// can't join Arabic letters or reorder RTL runs correctly, but a
// canvas 2D context does it for free. Shrinks the font until the text
// fits the canvas width, so long names don't clip. Returns the same
// {dataUrl, aspectRatio} shape as loadImageDataURL so callers can
// addImage() it identically.
const CANVAS_SCALE = 8; // render at high px-per-mm for crisp print output
const renderArabicNameDataURL = (text, { color = '#0f172a', maxWidthMM = 34, heightMM = 7 } = {}) => {
    if (!text) return null;
    try {
        const w = Math.round(maxWidthMM * CANVAS_SCALE);
        const h = Math.round(heightMM * CANVAS_SCALE);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        const fontStack = "'Segoe UI', Tahoma, 'Arial', sans-serif";
        let fontPx = h * 0.62;
        ctx.direction = 'rtl';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = color;
        const fits = (px) => {
            ctx.font = `700 ${px}px ${fontStack}`;
            return ctx.measureText(text).width <= w * 0.94;
        };
        while (fontPx > h * 0.28 && !fits(fontPx)) fontPx -= 1;
        ctx.clearRect(0, 0, w, h);
        ctx.font = `700 ${fontPx}px ${fontStack}`;
        ctx.fillText(text, w / 2, h / 2 + 1);
        return { dataUrl: canvas.toDataURL('image/png'), aspectRatio: w / h };
    } catch { return null; }
};

const generateQRDataURL = async (text) => {
    try {
        const dataUrl = await QRCode.toDataURL(text, {
            color: { dark: BADGE_COLORS.panelBg, light: '#ffffff' },
            width: 400,
            margin: 1,
        });
        return { dataUrl, aspectRatio: 1 };
    } catch { return null; }
};

const hexToRgb = (hex) => {
    const n = parseInt(hex.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const C = Object.fromEntries(Object.entries(BADGE_COLORS).map(([k, v]) => [k, hexToRgb(v)]));

const W = 54, H = 85.6; // CR80 portrait, millimetres
const ML = 5.5, MR = W - 5.5;

// Parses a 'YYYY-MM-DD' DB date string as a local-midnight Date so
// formatting never shifts a day off in either direction.
const parseDbDate = (s) => {
    if (!s) return null;
    const d = new Date(`${s}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

function drawFront(doc, member, assets, issuedDate, expiryDate) {
    const { icon, photo, watermarkDark, arabicName } = assets;

    // outer card
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...C.panelBorder);
    doc.setLineWidth(0.45);
    doc.roundedRect(1.5, 1.5, W - 3, H - 3, 3, 3, 'FD');

    // watermark — sits behind everything, low enough opacity to not
    // compete with the name/position text above it.
    if (watermarkDark) {
        const wmSize = 31;
        doc.addImage(watermarkDark.dataUrl, 'PNG', W / 2 - wmSize / 2, H * 0.62 - wmSize / 2, wmSize, wmSize);
    }

    let y = 5.4;

    // header — icon + two-tier company name, left aligned
    const iconSize = 4.6;
    if (icon) doc.addImage(icon.dataUrl, 'PNG', ML, y - 0.3, iconSize, iconSize / icon.aspectRatio);
    const textX = ML + iconSize + 1.6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.6);
    doc.setTextColor(...C.ink);
    doc.text(COMPANY_NAME_L1, textX, y + 1.8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.0);
    doc.setTextColor(...C.sub);
    const l2Lines = doc.splitTextToSize(COMPANY_NAME_L2, MR - textX);
    doc.text(l2Lines, textX, y + 4.4, { lineHeightFactor: 1.25 });

    y = Math.max(y + iconSize + 1.2, y + 4.4 + l2Lines.length * 1.4) + 2.0;

    // subtitle
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(4.6);
    doc.setTextColor(...C.tealDark);
    doc.text('EMPLOYEE ID CARD', W / 2, y, { align: 'center', charSpace: 0.15 });
    y += 2.6;

    // divider
    doc.setDrawColor(...C.teal);
    doc.setLineWidth(0.45);
    doc.line(ML, y, MR, y);
    y += 3.2;

    // photo — square (no reliable circular clip in jsPDF), double-ring frame
    const photoSize = 18;
    const photoX = W / 2 - photoSize / 2, photoY = y;
    doc.setDrawColor(...C.teal);
    doc.setLineWidth(0.55);
    doc.rect(photoX - 0.9, photoY - 0.9, photoSize + 1.8, photoSize + 1.8, 'S');
    if (photo) {
        doc.addImage(photo, 'PNG', photoX, photoY, photoSize, photoSize);
    } else {
        doc.setFillColor(...C.tealDark);
        doc.rect(photoX, photoY, photoSize, photoSize, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(255, 255, 255);
        const initials = (member.name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
        doc.text(initials, W / 2, photoY + photoSize / 2 + 1.4, { align: 'center' });
    }
    y = photoY + photoSize + 4.4;

    // name (EN) — shrink-to-fit, prefers a single centered line
    const nameMaxW = MR - ML;
    let nameFontSize = 11;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(nameFontSize);
    let nameLines = doc.splitTextToSize(member.name || '', nameMaxW);
    const widestLine = (lines) => Math.max(0, ...lines.map(l => doc.getTextWidth(l)));
    while ((nameLines.length > 1 || widestLine(nameLines) > nameMaxW) && nameFontSize > 7) {
        nameFontSize -= 0.5;
        doc.setFontSize(nameFontSize);
        nameLines = doc.splitTextToSize(member.name || '', nameMaxW);
    }
    nameLines = nameLines.slice(0, 2);
    doc.setTextColor(...C.ink);
    doc.text(nameLines, W / 2, y, { align: 'center', lineHeightFactor: 1.2 });
    y += (nameLines.length - 1) * nameFontSize * 0.42 + 1.2;

    // name (AR) — canvas-rendered image, correctly shaped/reordered
    if (arabicName) {
        const arW = Math.min(34, nameMaxW);
        const arH = arW / arabicName.aspectRatio;
        doc.addImage(arabicName.dataUrl, 'PNG', W / 2 - arW / 2, y, arW, arH);
        y += arH + 2.6;
    } else {
        y += 1.0;
    }

    // position
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.setTextColor(...C.tealDark);
    doc.text((member.position || '').toUpperCase(), W / 2, y, { align: 'center', charSpace: 0.1 });
    y += 5.0;

    // Nationality / Department / Blood Type — labeled 3-column grid
    const gridFields = [
        { label: 'NATIONALITY', value: member.nationality },
        { label: 'DEPARTMENT', value: member.department },
        { label: 'BLOOD TYPE', value: member.blood_type },
    ];
    const colW = (MR - ML) / 3;
    gridFields.forEach((f, i) => {
        const colCX = ML + colW * i + colW / 2;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(3.5);
        doc.setTextColor(...C.sub);
        doc.text(f.label, colCX, y, { align: 'center', charSpace: 0.1 });
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5.4);
        doc.setTextColor(...C.ink);
        doc.text(f.value || '—', colCX, y + 3.1, { align: 'center' });
        if (i > 0) {
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.15);
            const dx = ML + colW * i;
            doc.line(dx, y - 2.2, dx, y + 3.6);
        }
    });
    y += 6.4;

    // ── bottom block: Employee ID / VALID UNTIL ──
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(4.4);
    doc.setTextColor(...C.sub);
    doc.text('EMPLOYEE ID', W / 2, y, { align: 'center', charSpace: 0.15 });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.ink);
    doc.text(member.employee_id || 'Not assigned', W / 2, y + 3.4, { align: 'center' });
    y += 6.6;

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.15);
    doc.line(ML + 3, y, MR - 3, y);
    y += 3.4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(4.1);
    doc.setTextColor(...C.sub);
    doc.text('VALID UNTIL', W / 2, y, { align: 'center', charSpace: 0.15 });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.4);
    doc.setTextColor(...C.ink);
    doc.text(fmtDate(expiryDate), W / 2, y + 3.4, { align: 'center' });
}

function drawBack(doc, member, assets) {
    const { watermarkLight, qr, iconPhone, iconEmail, iconGlobe } = assets;
    const iconMap = { phone: iconPhone, email: iconEmail, globe: iconGlobe };

    doc.setFillColor(...C.panelBg);
    doc.setDrawColor(...C.panelBorder);
    doc.setLineWidth(0.45);
    doc.roundedRect(1.5, 1.5, W - 3, H - 3, 3, 3, 'FD');

    if (watermarkLight) {
        const wmSize = 39;
        doc.addImage(watermarkLight.dataUrl, 'PNG', W / 2 - wmSize / 2, H * 0.5 - wmSize / 2, wmSize, wmSize);
    }

    let y = 9.5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.4);
    doc.setTextColor(255, 255, 255);
    doc.text(COMPANY_NAME_L1, W / 2, y, { align: 'center' });
    y += 3.6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.2);
    doc.setTextColor(...C.onDarkMuted);
    const l2Lines = doc.splitTextToSize(COMPANY_NAME_L2, W - 16);
    doc.text(l2Lines, W / 2, y, { align: 'center', lineHeightFactor: 1.3 });
    y += l2Lines.length * 1.8 + 3.0;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(4.2);
    doc.setTextColor(...C.onDarkMuted);
    doc.text('EMPLOYEE IDENTIFICATION CARD', W / 2, y, { align: 'center', charSpace: 0.1 });
    y += 5.2;

    // QR code on a white plaque
    const qrBoxSize = 21, qrSize = 17.5;
    const qrBoxX = W / 2 - qrBoxSize / 2, qrBoxY = y;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 1.6, 1.6, 'F');
    if (qr) {
        doc.addImage(qr.dataUrl, 'PNG', W / 2 - qrSize / 2, qrBoxY + (qrBoxSize - qrSize) / 2, qrSize, qrSize);
    }
    y = qrBoxY + qrBoxSize + 4.2;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.6);
    doc.setTextColor(255, 255, 255);
    doc.text('Scan to Verify Employee', W / 2, y, { align: 'center' });
    y += 3.6;

    doc.setDrawColor(...C.onDarkMuted);
    doc.setLineWidth(0.15);
    doc.line(W / 2 - 17, y, W / 2 + 17, y);
    y += 3.0;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(3.6);
    doc.setTextColor(...C.onDarkMuted);
    const disclaimerLines = doc.splitTextToSize(CARD_DISCLAIMER, W - 16);
    doc.text(disclaimerLines, W / 2, y, { align: 'center', lineHeightFactor: 1.35 });
    y += disclaimerLines.length * 1.7 + 3.4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(4.0);
    doc.setTextColor(...C.onDarkMuted);
    doc.text('IF FOUND, PLEASE RETURN TO', W / 2, y, { align: 'center', charSpace: 0.1 });
    y += 4.4;

    // contact rows — icon + text, centered as a group
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.4);
    CONTACT_ROWS.forEach((row) => {
        const icon = iconMap[row.icon];
        doc.setTextColor(255, 255, 255);
        const textW = doc.getTextWidth(row.text);
        const iconSize = 3.0;
        const gap = 1.6;
        const totalW = (icon ? iconSize + gap : 0) + textW;
        const startX = W / 2 - totalW / 2;
        if (icon) doc.addImage(icon.dataUrl, 'PNG', startX, y - iconSize / 2 - 0.7, iconSize, iconSize / icon.aspectRatio);
        doc.text(row.text, startX + (icon ? iconSize + gap : 0), y);
        y += 4.6;
    });
}

/**
 * Builds the two-page badge PDF (CR80, 54mm × 85.6mm portrait) for one
 * member — page 1 is the front, page 2 the back. Returns the jsPDF
 * instance; the caller decides save() vs output() vs preview.
 */
export async function buildEmployeeBadgeDoc(member) {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [W, H] });

    const employeeIdentifier = member.employee_id || (member.id != null ? `RITIS-${member.id}` : 'RITIS-EMPLOYEE');

    const [icon, photo, watermarkDark, watermarkLight, iconPhone, iconEmail, iconGlobe, qr, arabicName] = await Promise.all([
        loadImageDataURL('/avatars/RITIS.png', 300),
        loadSquarePhotoDataURL(member.avatar, 300),
        loadImageDataURL('/avatars/RITIS-watermark-dark.png', 500),
        loadImageDataURL('/avatars/RITIS-watermark-light.png', 500),
        loadImageDataURL('/avatars/icon-phone.png', 240),
        loadImageDataURL('/avatars/icon-email.png', 240),
        loadImageDataURL('/avatars/icon-globe.png', 240),
        generateQRDataURL(`RITIS-EMPLOYEE:${employeeIdentifier}`),
        Promise.resolve(renderArabicNameDataURL(member.name_arabic)),
    ]);

    const issuedDate = parseDbDate(member.badge_issue_date) || new Date();
    const expiryDate = parseDbDate(member.badge_expiry_date)
        || new Date(issuedDate.getFullYear() + DEFAULT_VALIDITY_YEARS, issuedDate.getMonth(), issuedDate.getDate());

    drawFront(doc, member, { icon, photo, watermarkDark, arabicName }, issuedDate, expiryDate);
    doc.addPage([W, H], 'portrait');
    drawBack(doc, member, { watermarkLight, qr, iconPhone, iconEmail, iconGlobe });

    return doc;
}

export async function printEmployeeBadge(member) {
    const doc = await buildEmployeeBadgeDoc(member);
    const safeName = (member.name || 'Employee').replace(/[^\w-]+/g, '_');
    doc.save(`Badge_${safeName}.pdf`);
}
